import { type SxProps, TextField, type Theme, Typography } from '@mui/material';
import React from 'react';

interface TextInputProps {
    name?: string;
    value: string;
    password?: true;
    placeholder?: string;
    disabled?: boolean;
    helperText?: string;
    size?: 'medium' | 'small' | 'verysmall';
    isAI?: true;
    sx?: SxProps<Theme>;
    maxLength?: number;
    rows?: number;
    onChange?: (val: string) => void;
    onBlur?: () => void;
}

/**
 * Modern TextInput component with MUI styling.
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The rendered TextInput component.
 */
function TextInput({
    name,
    value,
    password,
    placeholder,
    disabled,
    size,
    helperText,
    isAI,
    sx,
    maxLength,
    rows,
    onChange,
    onBlur,
}: TextInputProps) {
    const innerSize = size || 'medium';
    const inputSize = innerSize === 'medium' || innerSize === 'small' ? innerSize : 'small';

    if (!onChange) {
        return (
            <Typography variant="body2" sx={{ p: 1 }}>
                {value}
            </Typography>
        );
    }

    return (
        <TextField
            variant="outlined"
            name={name}
            autoComplete={password ? 'new-password' : undefined}
            type={password ? 'password' : 'text'}
            disabled={disabled}
            size={inputSize}
            value={value}
            placeholder={placeholder}
            helperText={helperText}
            multiline={!password}
            rows={rows}
            inputProps={{ maxLength }}
            onBlur={onBlur}
            onChange={(event) => onChange(event.target.value)}
            sx={{
                width: 'auto',
                minWidth: innerSize === 'verysmall' ? '120px' : innerSize === 'small' ? '160px' : '200px',
                '& .MuiInputBase-input': {
                    fontSize: '0.875rem',
                    ...(innerSize === 'verysmall' && { padding: '6px 8px' }),
                    ...(rows && { minHeight: 'auto' }),
                },
                '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    ...(isAI && {
                        backgroundColor: (theme) =>
                            theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(25, 118, 210, 0.04)',
                    }),
                },
                ...sx,
            }}
        />
    );
}

export default TextInput;
